import React, { memo, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { createPortal } from 'react-dom';
import loadable from '@loadable/component';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Lobby, { type Section } from 'modules/Lobby';
import { isLoading } from 'lib/redux-utils';
import type { Collection } from 'lib/LobbyEntities';
import Header, { HeaderSize } from 'components/Header';
import Footer from 'components/Footer';
import VerticalLayout from 'components/VerticalLayout';
import TopTabBar from 'components/TopTabBar';
import Builder from '../src/Builder';
import DynamicLobbyContext from '../src/DynamicLobbyContext';
import { GridSkeleton, TileSkeleton } from '../src/Skeleton';
import './viewAll.css';

const Grid = loadable(
  () => import(/* webpackPrefetch: true, webpackChunkName: "LobbyViewAll_Grid" */ './src/Grid'),
  {
    fallback: <GridSkeleton />
  }
);

const GameTile = loadable(
  () =>
    import(/* webpackPrefetch: true, webpackChunkName: "LobbyViewAll_SlotTile" */ './src/GameTile'),
  {
    fallback: <TileSkeleton className="dynamicLobby__skeleton-viewAllSlotTile" />
  }
);

const mapping = {
  List: Grid,
  GameTile
};

type useParamsProps = {
  section: Section;
  category: string;
  entityRef: string;
};

const ViewAll = () => {
  const dispatch = useAppDispatch();
  const { section, category, entityRef } = useParams<useParamsProps>();
  const backLinkTo = `/secure/lobby/${section}/${category}`;
  const listAccessRef = `List__${section}__${category}__${entityRef}`;
  const collectionAccessRef = `Collection__${entityRef}`;

  const listCollection = useAppSelector((state) =>
    Lobby.selectors.getEntityByKey(state, collectionAccessRef)
  ) as Collection | undefined;

  const loading = useAppSelector((state) =>
    isLoading(state, Lobby.actionTypes.AT.GET_COMPONENT_CONTENT._)
  );

  useEffect(() => {
    void Lobby.actions.getComponentContent({
      sort: 'position,ASC',
      offset: 0,
      limit: 30,
      section,
      category,
      componentRef: entityRef
    })(dispatch);
  }, [dispatch, section, category, entityRef]);

  const contextValue = useMemo(
    () => ({
      section,
      category
    }),
    [section, category]
  );

  const topTabBar = <TopTabBar isLobby />;
  const portalTopElement = document.getElementById('navBar');

  return (
    <div
      className="viewAll"
      data-amp-id="gamelist"
      data-amp-list-ref={entityRef}
      data-amp-list-position="1"
    >
      <Header backButtonLink={backLinkTo} title={listCollection?.title} size={HeaderSize.SM} />
      {topTabBar}
      {/* Why render the portal additionally instead of conditionally? */}
      {/* We render both and depend on CSS to display the correct version because it is faster */}
      {/* despite the duplication. A render prop + Media wrapper would be slower to update. */}
      {portalTopElement && createPortal(topTabBar, portalTopElement)}
      <DynamicLobbyContext.Provider value={contextValue}>
        <VerticalLayout className="viewAll__content">
          {!listCollection && loading && <GridSkeleton />}
          {listCollection && <Builder mapping={mapping} accessRef={listAccessRef} />}
          <Footer />
        </VerticalLayout>
      </DynamicLobbyContext.Provider>
    </div>
  );
};

export default memo(ViewAll);
